import { AbsoluteCenter } from "@chakra-ui/react";
import { t } from "i18next";
import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import ToggleFollow from "../../components/api/mutations/ToggleFollow";
import useUserProfileQuery from "../../components/api/queries/useUserProfileQuery";
import ProfileLineButton from "../../components/buttons/ProfileLineButton";
import RegularSolidButton from "../../components/buttons/RegularSolidButton";
import { FollowedDialog } from "../../components/fan-buddy/FollowedDialog";
import { MBTICard } from "../../components/fan-buddy/MBTICard";
import StickyHeader from "../../components/StickyHeader";
import LightColors from "../../constants/LightColors";
import { ReactComponent as CheckIcon } from "../../icons/check.svg";
import { FAN_BUDDY_MBTI } from "../../utils/FanBuddyHelper";
import { ellipsis } from "../../utils/TextUtil";
import { isDarkMode } from "../../utils/ThemeHelper";

const GradientCard = styled.div`
  position: relative;
  border-radius: 18px;
  background: ${({ isDarkMode }) => (isDarkMode ? "#181818" : "#fff")};
  box-shadow: ${({ isDarkMode }) =>
    isDarkMode
      ? "0 4px 32px 0 rgba(0,0,0,0.50)"
      : "0 4px 32px 0 rgba(203,227,255,0.24)"};
  overflow: visible;
  width: 144px;
  height: 204px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1;
 &::before {
  content: "";
  position: absolute;
  inset: -18px;
  border-radius: 24px;
  pointer-events: none;
  z-index: 0;
  opacity: ${({ isDarkMode }) => (isDarkMode ? 0.85 : 0.9)};
  filter: blur(${({ isDarkMode }) => (isDarkMode ? "24px" : "28px")});
  background: ${({ isDarkMode }) =>
    isDarkMode
      ? `
        /* 바깥쪽에만 컬러가 남도록 stop 위치 조정 */
        linear-gradient(135deg,
          rgba(203,227,255,0) 0%,
          rgba(0,0,0,0.7) 60%,
          rgba(151,255,198,0.64) 100%
        ),
        linear-gradient(135deg,
          rgba(0,0,0,0.7) 0%,
          rgba(0,0,0,1) 6%,
          rgba(0,0,0,0.7) 20%,
          rgba(0,0,0,1) 36%,
          rgba(0,0,0,0.48) 70%,
          rgba(0,0,0,1) 100%
        )
      `
      : `
        linear-gradient(135deg,
          rgba(203,227,255,0) 0%,
          rgba(255,255,255,0.7) 60%,
          rgba(151,255,198,0.64) 100%
        ),
        linear-gradient(135deg,
          rgba(255,255,255,0.7) 0%,
          rgba(255,255,255,1) 6%,
          rgba(255,255,255,0.3) 20%,
          rgba(255,255,255,1) 36%,
          rgba(255,255,255,0.48) 70%,
          rgba(255,255,255,1) 100%
        )
      `};
  mix-blend-mode: lighten;
}
`;
const MBTICardContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 24px 0px 36px 0px;
`

const CharacteristicTitle = styled.div`
  font-size: 13px;
  line-height: 22px;
  color: ${({ isDarkMode }) =>
    isDarkMode
      ? "var(--text-main-lv2, #AAABAC)"
      : "var(--text-main-lv2, #666666)"};
  font-weight: 600;
  margin-bottom: 1px;
`

const Characteristic = styled.div`
  font-size: 13px;
  color: ${({ isDarkMode }) =>
    isDarkMode
      ? "var(--text-main-lv3, #AAABAC)"
      : "var(--text-main-lv3, #666666)"};
`;

const Bullet = styled.span`
  color: ${({ isDarkMode }) =>
    isDarkMode
      ? "var(--text-main-lv3, #AAABAC)"
      : "var(--text-main-lv3, #666666)"};
`;

const Divider = styled.hr`
  margin: 16px 0;
  border: 0;
  border-top: 1px solid transparent;
  height: 1px;
  background: ${({ isDarkMode }) =>
    isDarkMode
      ? "var(--separator-lv1, #303030)"
      : "var(--separator-lv1, #F6F6F6)"};
`;

function FanBuddyMatchingPage() {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);

  const [isFollowed, setIsFollowed] = useState(false);
  const [isShowFollowedDialog, setIsShowFollowedDialog] = useState(false);

  // MyUserProfileId,
  const sourceUserProfileId = searchParams.get("sourceUserProfileId")
  const sourceUserProfile = useUserProfileQuery(sourceUserProfileId)

  const targetUserProfileId = searchParams.get("targetUserProfileId")
  const targetUserProfile = useUserProfileQuery(targetUserProfileId)

  useEffect(() => {
    if (targetUserProfile) {
      setIsFollowed(targetUserProfile.isFollowed)
    }
  }, [targetUserProfile])

  const mutualKeywords = useMemo(() => {
    const sourceKeywords = sourceUserProfile?.buddyPreference?.keywords?.map(k => k.name) || []
    const targetKeywords = targetUserProfile?.buddyPreference?.keywords?.map(k => k.name) || []
    return sourceKeywords.filter(keyword => targetKeywords.includes(keyword))
  }, [sourceUserProfile, targetUserProfile])

  const handleFollow = async () => {
    const response = await ToggleFollow({
      isFollowed: isFollowed,
      currentUserProfileId: sourceUserProfileId,
      followUserProfileId: targetUserProfileId
    });

    if (response?.ok) {
      if (!isFollowed) {
        setIsShowFollowedDialog(true);
      }
      setIsFollowed(!isFollowed);
    }
  }

  if(isEmpty(sourceUserProfile) || isEmpty(targetUserProfile)) {
    return (<></>)
  }

  return (
    <div className="bg-white dark:bg-[#222222] min-h-screen w-full">
      <StickyHeader
        title={t('FanBuddyMatchingPage.pageTitle')}
        showBackButton={true}
        onBack={() => navigate(-1)}
      />

      <AbsoluteCenter
        style={{
          padding: '0 24px',
          width: '100%',
          maxWidth: 'calc(var(--chakra-sizes-xl))'
        }}
      >
        <>
          <div className="flex flex-col items-center gap-[10px] pb-[20px]">
            <h2 className='text-[17px] leading-[20px] text-main-light-4 dark:text-main-dark-4 font-bold text-center'>
              {t('FanBuddyMatchingPage.title')}
            </h2>
            <h1 className='text-[24px] text-primary-light dark:text-textPrimary-dark font-bold text-center'>
              {(() => {
                // TODO : Refactor matching score
                const rank = FAN_BUDDY_MBTI[
                  sourceUserProfile.buddyPreference?.mbti
                ]?.compatibleRanking.indexOf(targetUserProfile.buddyPreference?.mbti);
                if (rank < 4) return t('FanBuddyMatchingPage.perfectMatch');
                if (rank < 8) return t('FanBuddyMatchingPage.goodMatch');
                if (rank < 12) return t('FanBuddyMatchingPage.goodFriend');
                return t('FanBuddyMatchingPage.notGood');
              })()}
            </h1>
          </div>
          <MBTICardContainer>
            <GradientCard
              isDarkMode={isDarkMode()}
              style={{
                flex: 1,
                transform: "rotate(-6deg)  scale(0.85)",
                willChange: "transform",
                marginRight: "-6px",
              }}
            >
              {sourceUserProfile.buddyPreference && (
                <MBTICard
                  mbti={sourceUserProfile.buddyPreference?.mbti}
                  noAnimation={true}
                  isDarkMode={isDarkMode()}
                />
              )}
            </GradientCard>
            <GradientCard
              isDarkMode={isDarkMode()}
              style={{
                flex: 1,
                transform: "rotate(6deg)  scale(0.85)",
                willChange: "transform",
                marginLeft: "-6px",
              }}
            >
              {targetUserProfile.buddyPreference && (
                <MBTICard
                  mbti={targetUserProfile.buddyPreference.mbti}
                  isMine={false}
                  noAnimation={true}
                  isDarkMode={isDarkMode()}
                />
              )}
            </GradientCard>
          </MBTICardContainer>

          {/* <MBTICardContainer>
            <div
              style={{
                flex: 1,
                transform: 'rotate(-6deg) translateY(18px) scale(0.85)',
                willChange: 'transform',
                marginRight: '-6px'
              }}
            >
              {sourceUserProfile.buddyPreference && (
                <MBTICard mbti={sourceUserProfile.buddyPreference?.mbti} noAnimation={true} />
              )}
            </div>
            <div
              style={{
                flex: 1,
                transform: 'rotate(6deg) translateY(18px) scale(0.85)',
                willChange: 'transform',
                marginLeft: '-6px'
              }}
            >
              {targetUserProfile.buddyPreference && (
                <MBTICard
                  mbti={targetUserProfile.buddyPreference.mbti}
                  isMine={false}
                  noAnimation={true}
                />
              )}
            </div>
          </MBTICardContainer> */}
          <CharacteristicBox
            sourceUserProfile={sourceUserProfile}
            targetUserProfile={targetUserProfile}
            mutualKeywords={mutualKeywords}
            isDarkMode={isDarkMode()}
          />
        </>
      </AbsoluteCenter>
      <div
        className="absolute bottom-0 w-full px-[24px]"
        style={{
          marginBottom: 'calc(24px + env(safe-area-inset-bottom))',
          maxWidth: 'calc(var(--chakra-sizes-xl))'
        }}
      >
        {!isFollowed && (
          // <ProfileLineButton
          //   text={t('following')}
          //   onClick={handleFollow}
          //   fontSize="17px"
          //   style={{
          //     background: LightColors.secondary,
          //     padding: '11px 12px',
          //     color: LightColors.primary,
          //     display: 'flex',
          //     justifyContent: 'center',
          //     alignItems: 'center',
          //     textAlign: 'center',
          //     maxWidth: 'none'
          //   }}
          //   rightIcon={
          //     <div style={{ marginLeft: '4px' }}>
          //       <CheckIcon fill={LightColors.onLabel} width={'14px'} height={'14px'} />
          //     </div>
          //   }
          // />
          <RegularSolidButton
            style={{ padding: '16px 12px', fontSize: '17px', width: '100%' }}
            text={t('FanBuddyMatchingPage.followButton', { nickname: targetUserProfile.nickname })}
            onClicked={handleFollow}
          />
        )}
        {isShowFollowedDialog && (
          <FollowedDialog
            isAlertOpen={isShowFollowedDialog}
            setIsAlertOpen={setIsShowFollowedDialog}
            isFollowed={targetUserProfile?.isFollowing}
          />
        )}
      </div>
    </div>
  );
}

const CharacteristicBox = ({sourceUserProfile, targetUserProfile, mutualKeywords, isDarkMode}) => {
  const mutualMembers = targetUserProfile.mutualMembers || []
  const hasMutualData = mutualKeywords?.length > 0 || mutualMembers?.length > 0

  return (
    <div className="
      border-[2px]
      rounded-[12px]
      w-full text-left py-[12px] px-[18px] border-separator-light-1 dark:border-separator-dark-1">
      <div className="list-none p-0 m-0 text-[14px] text-main-light-2 dark:text-main-dark-2">
        <CharacteristicTitle isDarkMode={isDarkMode}>{`${
          FAN_BUDDY_MBTI[targetUserProfile.buddyPreference?.mbti]?.name
        }인 ${ellipsis(targetUserProfile.nickname, 5)}님은 이렇대요..!`}</CharacteristicTitle>
        <div className="mb-4">
          {FAN_BUDDY_MBTI[sourceUserProfile.buddyPreference?.mbti]?.descriptions?.map(
            (desc, index) => (
              <div
                className="flex flex-row gap-[6px] items-center"
                style={{ lineHeight: '20px' }}
                key={index}
              >
                <Bullet isDarkMode={isDarkMode}>•</Bullet>
                <Characteristic isDarkMode={isDarkMode}>{desc}</Characteristic>
              </div>
            )
          )}
        </div>

        {hasMutualData && (
          <>
            <Divider isDarkMode={isDarkMode} />
            <CharacteristicTitle isDarkMode={isDarkMode}>
              {`${t('FanBuddyMatchingPage.mutualKeywordsTitle', {
                nickname: targetUserProfile.nickname
              })}`}
            </CharacteristicTitle>
            <div className="mt-1">
              {mutualKeywords.length > 0 && (
                <div
                  className="flex flex-row gap-[6px] items-center"
                  style={{ lineHeight: '20px' }}
                >
                  <Bullet isDarkMode={isDarkMode}>•</Bullet>
                  <Characteristic isDarkMode={isDarkMode}>
                    {t('FanBuddyMatchingPage.mutualKeywordsDescription', {
                      keywords: mutualKeywords.join(', ')
                    })}
                  </Characteristic>
                </div>
              )}

              {mutualMembers.length > 0 && (
                <div
                  className="flex flex-row gap-[6px] items-center"
                  style={{ lineHeight: '20px' }}
                >
                  <Bullet isDarkMode={isDarkMode}>•</Bullet>
                  <Characteristic isDarkMode={isDarkMode}>
                    {t('FanBuddyMatchingPage.mutualMembersDescription', {
                      members: mutualMembers.map((m) => m.name).join(', ')
                    })}
                  </Characteristic>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default FanBuddyMatchingPage
